import type { SearchConfig } from "./buildSearchUrl";

let BRANDNAME = process.env.NEXT_PUBLIC_BRAND_NAME
  ? process.env.NEXT_PUBLIC_BRAND_NAME.toLowerCase()
  : "pei";

let PEIURL = "";
const BASEURL =
  process.env.NEXT_PUBLIC_BASE_URL ||
  process.env.BASE_URL ||
  "https://ra-api.stage.pei-services.com/api";
const isStageUrl = BASEURL?.includes("stage") ? true : false;

switch (BRANDNAME) {
  case "pei":
    PEIURL = isStageUrl
      ? "https://stage.privateequityinternational.com"
      : "https://www.privateequityinternational.com";
    break;
  case "npm":
    PEIURL = isStageUrl
      ? "https://stage.newprivatemarkets.com"
      : "https://www.newprivatemarkets.com";
    break;
  case "pdi":
    PEIURL = isStageUrl
      ? "https://stage.pei-privatecredit.com"
      : "https://www.pei-privatecredit.com";
    break;
  case "buyout":
    PEIURL = isStageUrl ? "https://stage.buyoutsinsider.com" : "https://www.buyoutsinsider.com";
    break;
  case "pere":
    PEIURL = isStageUrl
      ? "https://stage.pei-privaterealestate.com"
      : "https://www.pei-privaterealestate.com";
    break;
  case "ii":
    PEIURL = isStageUrl
      ? "https://stage.infrastructureinvestor.com"
      : "https://www.infrastructureinvestor.com";
    break;
  case "pehub":
    PEIURL = isStageUrl ? "https://stage.pehub.com" : "https://www.pehub.com";
    break;
  case "si":
    PEIURL = isStageUrl
      ? "https://stage.pei-secondariesinvestor.com"
      : "https://www.pei-secondariesinvestor.com";
    break;
  case "agri":
    PEIURL = isStageUrl ? "https://stage.agriinvestor.com" : "https://www.agriinvestor.com";
    break;
  default:
    BRANDNAME = "pei";
    PEIURL = isStageUrl
      ? "https://stage.privateequityinternational.com"
      : "https://www.privateequityinternational.com";
    break;
}

// Brands whose site has moved onto the OBX platform under a new domain
// (WEB-29766) *and* whose on-site search is served by OBX. Register a brand here
// rather than branching on its name downstream — the entry drives on-site search.
// A brand whose navigation has moved but whose search has not belongs in
// LEGACY_SEARCH_HOSTS below instead (WEB-33210).
const OBX_HOSTS: Record<string, { prod: string; stage: string }> = {
  pere: {
    prod: "https://www.pei-privaterealestate.com",
    stage: "https://stage.pei-privaterealestate.com",
  },
};
const obxBrand = OBX_HOSTS[BRANDNAME];
const OBX_HOST = obxBrand ? (isStageUrl ? obxBrand.stage : obxBrand.prod) : undefined;

// Public canonical host used for externally-visible artefacts: sitemap <loc>
// entries (utils/common.ts) and the <link rel="canonical"> tag on related-news
// pages (pages/[Institution].tsx). Declared per brand via the CANONICAL_URL_<BRAND>
// environment variable and resolved in next.config.js — see README.
//
// For most brands this matches PEIURL. It is declared separately so a brand's SEO
// host can diverge from its navigation host during a migration — the OBX brand
// migrations (WEB-29766 PERE, WEB-30496 Credit) moved the sitemap onto the new
// brand domain ahead of navigation. Since WEB-33210 moved navigation onto the
// current public domains, the two agree again for II/PERE/PDI/SI.
const CANONICAL_PEIURL = process.env.NEXT_PUBLIC_CANONICAL_URL;

if (!CANONICAL_PEIURL) {
  throw new Error(
    'NEXT_PUBLIC_CANONICAL_URL is not set. It is derived from CANONICAL_URL_<BRAND> in ' +
      'next.config.js, so this means either the build config did not run or a test ' +
      'environment is missing the value — see jest.setup.js and README.'
  );
}

// The host these brands navigated to BEFORE WEB-33210 moved navigation onto their
// current public domains. Two consumers still need it, and for both the pin is
// load-bearing rather than tidiness:
//
//   * On-site search. The OBX sites do not serve the WordPress `?s=` form. Asking
//     them to answers HTTP 200 with the *homepage* and the query silently dropped
//     — a failure no link-check or status-code assertion would catch. Search moves
//     to the OBX `/search/?k=` form separately in WEB-33225.
//   * The sitemap-membership probe (api/index.ts pingOldProfile), which checks for
//     the old WordPress profile page and was deliberately moved off the www host
//     by CP-150 to keep per-profile build requests away from it.
//
// PERE is listed for the probe only: its search already resolves through
// OBX_HOSTS above, so the search branch never reads this entry for it.
const LEGACY_BRAND_HOSTS: Record<string, { prod: string; stage: string }> = {
  ii: {
    prod: "https://classic.infrastructureinvestor.com",
    stage: "https://stage.classic.infrastructureinvestor.com",
  },
  pere: {
    prod: "https://www.perenews.com",
    stage: "https://stage.perenews.com",
  },
  pdi: {
    prod: "https://www.privatedebtinvestor.com",
    stage: "https://stage.privatedebtinvestor.com",
  },
  si: {
    prod: "https://www.secondariesinvestor.com",
    stage: "https://stage.secondariesinvestor.com",
  },
};
const legacyBrand = LEGACY_BRAND_HOSTS[BRANDNAME];
const LEGACY_HOST = legacyBrand
  ? isStageUrl
    ? legacyBrand.stage
    : legacyBrand.prod
  : undefined;

// On-site search host and query form. OBX brands serve search from the OBX host;
// brands with a legacy host pinned above keep it; everyone else still runs the
// legacy WordPress search on the host their UI navigates to.
const SEARCH: SearchConfig = OBX_HOST
  ? { host: OBX_HOST, platform: "obx" }
  : { host: LEGACY_HOST ?? PEIURL, platform: "wordpress" };

export const URL: {
  shouldGenerateSitemap: boolean;
  brand: string;
  brandURL: string;
  environment: string;
  sessionid: string | undefined;
  baseURL: string;
  basePEIURL: string;
  canonicalPEIURL: string;
  legacyProfileProbeURL: string;
  search: SearchConfig;
  investmentAppetiteLink: {
    main: string;
    route: string;
  };
  commonRoute: string;
  routes: {
    blaize: string;
    relatedNews: string;
    contactInfo: string;
    fundManaged: string;
    fundCommitment: string;
    lpFundCommitment: string;
  };
} = {
  shouldGenerateSitemap: process.env.NEXT_PUBLIC_GENERATE_SITEMAP
    ? JSON.parse(process.env.NEXT_PUBLIC_GENERATE_SITEMAP.toLowerCase())
    : false,
  brand: BRANDNAME,
  brandURL: "https://pei.group/",
  environment: isStageUrl ? "stage" : "prod",
  sessionid: process.env.SESSION_ID,
  baseURL: BASEURL,
  basePEIURL: PEIURL,
  canonicalPEIURL: CANONICAL_PEIURL,
  legacyProfileProbeURL: LEGACY_HOST ?? PEIURL,
  search: SEARCH,
  investmentAppetiteLink: {
    main: "/database/institution-profile/id/",
    route: "/lp-investment-appetite",
  },
  commonRoute: "/institutions/",
  routes: {
    blaize: "/blaize/datalayer",
    relatedNews: "/related-news",
    contactInfo: "/contact-info",
    fundManaged: "/funds-managed",
    fundCommitment: "/fund-commitments",
    lpFundCommitment: "/lp-fund-commitments",
  },
};
